import { AfterViewInit, Component, ElementRef, OnDestroy, viewChild, input } from '@angular/core';
import { CdkDrag } from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs';
import { debounceTime, filter, map, takeUntil, throttleTime } from 'rxjs/operators';
import { CdkScrollable } from '@angular/cdk/scrolling';

/**
 * A fully custom scrollbar component based on Material CDK drag&drop. <br/>
 * It hides the native browser scrollbars and replaces them with custom elements. <br/>
 * The reason behind this component is that browser scrollbars don't let you put scrollbars on the top of the content,
 * and they are not customizable enough to fit the <strong>CUSTOM</strong> design of the application. <br/>
 * It can be customized with CSS to the client's will. <br/> <br/>
 * Ohh, and what's this smell? Can't you recognize it? It's the smell of code repetition!
 *  You can see similar code in the activities calendar component, but this component is more generic.
 */
@Component({
    selector: 'app-overlay-scrollbar',
    imports: [CdkDrag, CdkScrollable],
    templateUrl: './overlay-scrollbar.component.html',
    styleUrl: './overlay-scrollbar.component.scss'
})
export class OverlayScrollbarComponent implements AfterViewInit, OnDestroy {
  /**
   * The time in milliseconds to throttle the scroll events. <br/>
   * In theory 10ms means 100fps (it's more than enough), 17ms would mean about 60fps, adjust this value to your needs. <br/>
   * This is to prevent the scroll event firing too often and causing performance issues.
   */
  throttleTime = input(10);

  contentWrapper = viewChild<ElementRef<HTMLDivElement>>('contentWrapper');
  scrollbarTrackY = viewChild<ElementRef<HTMLDivElement>>('scrollbarTrackY');
  scrollbarThumbY = viewChild('scrollbarThumbY', { read: CdkDrag });
  scrollbarTrackX = viewChild<ElementRef<HTMLDivElement>>('scrollbarTrackX');
  scrollbarThumbX = viewChild('scrollbarThumbX', { read: CdkDrag });

  // to prevent the scroll event firing when the scrollbar is moved by drag - it would cause an infinite loop
  scrollEventFromDragY = false;
  scrollEventFromDragX = false;
  // emits the scroll event when the view is scrolled on either Y or X axis
  scrolled = new Subject<Event>();
  isDestroyed$ = new Subject<boolean>();

  ngAfterViewInit(): void {
    this.handleScrollEvents();
    this.handleScrollTrackMovementY();
    this.handleScrollTrackMovementX();
  }

  ngOnDestroy() {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }

  /**
   * The subscribe method is called when the view is scrolled by mouse wheel or touchpad scroll. <br/>
   * It updates the scroll thumb position (CDK draggable element) in the scrollbar.
   * @private
   */
  private handleScrollEvents() {
    this.scrolled
      .pipe(
        takeUntil(this.isDestroyed$),
        filter((event) => !!event.target),
        throttleTime(10), // 10ms - 100fps in theory
        // to prevent the scroll event firing when the scrollbar is moved by drag - it would cause an infinite loop
        filter((_) => !this.scrollEventFromDragY && !this.scrollEventFromDragX),
        map((event) => {
          const target = event.target as HTMLElement;
          // the scrollable area is the difference between the scrollHeight and the offsetHeight
          const scrollableHeight = target.scrollHeight - target.offsetHeight;
          const scrollableWidth = target.scrollWidth - target.offsetWidth;

          // scrolledRatio is a number between 0 and 1, representing the position of the shown area in the scrollable area
          const scrolledRatioY = target.scrollTop / scrollableHeight;
          const scrolledRatioX = target.scrollLeft / scrollableWidth;

          return { scrolledRatioY, scrolledRatioX };
        }),
      )
      .subscribe(({ scrolledRatioY, scrolledRatioX }) => {
        // console.log('scrolled', event, scrolledRatioY, scrolledRatioX);
        const thumbY = this.scrollbarThumbY().getRootElement() as unknown as HTMLElement;
        const thumbX = this.scrollbarThumbX().getRootElement() as unknown as HTMLElement;
        const scrollbarY = this.scrollbarTrackY().nativeElement;
        const scrollbarX = this.scrollbarTrackX().nativeElement;

        const scrollableHeightInScrollbarY = scrollbarY.offsetHeight - thumbY.offsetHeight;
        this.scrollbarThumbY().setFreeDragPosition({
          x: 0,
          y: scrollableHeightInScrollbarY * scrolledRatioY,
        });

        const scrollableWidthInScrollbarX = scrollbarX.offsetWidth - thumbX.offsetWidth;
        this.scrollbarThumbX().setFreeDragPosition({
          x: scrollableWidthInScrollbarX * scrolledRatioX,
          y: 0,
        });
      });
  }

  /**
   * This observable is fired when the user drags the scrollbar thumb on Y axis. <br/>
   * It actually moves the view inside the wrapper. <br/>
   * @private
   */
  private handleScrollTrackMovementY() {
    this.scrollbarThumbY()
      .moved.pipe(takeUntil(this.isDestroyed$), throttleTime(this.throttleTime()))
      .subscribe((event) => {
        if (!event?.source) {
          return;
        }

        // the thumb's position from the top of the scrollbar
        const scrolledFromTop = event.source.getFreeDragPosition()?.y;
        const trackBarHeight = event.source.getRootElement()?.offsetHeight;
        const containerHeight = this.scrollbarTrackY().nativeElement.offsetHeight;
        // scrolledRatio is a number between 0 and 1, representing the position of the scrollbar in the scrollable area
        const scrolledRatio = scrolledFromTop / (containerHeight - trackBarHeight);

        const scrollAreaScrollHeight = this.contentWrapper().nativeElement.scrollHeight;
        const scrollAreaOffsetHeight = this.contentWrapper().nativeElement.offsetHeight;
        // user can scroll only in the difference, if scrollHeight == offsetHeight, there is nothing to scroll
        const diff = scrollAreaScrollHeight - scrollAreaOffsetHeight;
        this.scrollEventFromDragY = true;
        this.contentWrapper().nativeElement.scrollTo({ top: diff * scrolledRatio });
      });

    this.scrollbarThumbY()
      .moved.pipe(takeUntil(this.isDestroyed$), debounceTime(200))
      .subscribe(() => {
        this.scrollEventFromDragY = false;
      });
  }

  /**
   * This observable is fired when the user drags the scrollbar thumb on X axis. <br/>
   * It actually moves the view inside the wrapper. <br/>
   * @private
   */
  private handleScrollTrackMovementX() {
    this.scrollbarThumbX()
      .moved.pipe(takeUntil(this.isDestroyed$), throttleTime(this.throttleTime()))
      .subscribe((event) => {
        if (!event?.source) {
          return;
        }

        // the thumb's position from the top of the scrollbar
        const scrolledFromLeft = event.source.getFreeDragPosition()?.x;
        const trackBarWidth = event.source.getRootElement()?.offsetWidth;
        const containerWidth = this.scrollbarTrackX().nativeElement.offsetWidth;
        // scrolledRatio is a number between 0 and 1, representing the position of the scrollbar in the scrollable area
        const scrolledRatio = scrolledFromLeft / (containerWidth - trackBarWidth);

        const scrollAreaScrollWidth = this.contentWrapper().nativeElement.scrollWidth;
        const scrollAreaOffsetWidth = this.contentWrapper().nativeElement.offsetWidth;
        // user can scroll only in the difference, if scrollHeight == offsetHeight, there is nothing to scroll
        const diff = scrollAreaScrollWidth - scrollAreaOffsetWidth;
        this.scrollEventFromDragX = true;
        this.contentWrapper().nativeElement.scrollTo({ left: diff * scrolledRatio });
      });

    this.scrollbarThumbX()
      .moved.pipe(takeUntil(this.isDestroyed$), debounceTime(200))
      .subscribe(() => {
        this.scrollEventFromDragX = false;
      });
  }
}
